import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import { fetchAgentAlbertAlertShift } from "../../utils/api";
import "../AipAnalysis.css";

const ShiftCheckInOutDetailModal = ({ alert, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!isOpen || !alert?.clientId || !alert?.appointmentId) {
      setPayload(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setPayload(null);

    fetchAgentAlbertAlertShift({
      clientId: alert.clientId,
      appointmentId: alert.appointmentId,
      stage: alert.source,
    })
      .then((data) => {
        if (cancelled) return;
        setPayload(data || null);
        if (!data?.session) {
          setError("No shift check-in / check-out data found for this alert.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setPayload(null);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load shift questions.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, alert?.clientId, alert?.appointmentId, alert?.source]);

  const session = payload?.session;
  const stages = Array.isArray(payload?.stages) ? payload.stages : [];
  const highlightStage = String(
    session?.highlightStage || alert?.source || "",
  ).toLowerCase();

  const title =
    session?.modalTitle ||
    (session?.isArchived
      ? "Archived Shift Check-in & Check-out Questions"
      : "Shift Check-in & Check-out Questions");

  const renderStageTable = (stageBlock) => {
    const isCheckIn = stageBlock.stage === "check_in";
    const isHighlighted =
      highlightStage && stageBlock.stage === highlightStage;
    const rows = Array.isArray(stageBlock.rows) ? stageBlock.rows : [];

    return (
      <div
        className={`qa-table-card ${isCheckIn ? "check-in" : "check-out"}${
          isHighlighted ? " featured" : ""
        }`}
        key={stageBlock.stage}
        style={
          isHighlighted
            ? { boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.25)" }
            : undefined
        }
      >
        <div className="qa-table-head">
          <div className={`stage-tag ${isCheckIn ? "check-in" : "check-out"}`}>
            {stageBlock.label}
          </div>
          <div className="qa-table-meta">
            {stageBlock.questionSetTitle || "Question set"}
            {stageBlock.questionCount
              ? ` • ${stageBlock.questionCount} question${
                  stageBlock.questionCount === 1 ? "" : "s"
                }`
              : ""}
          </div>
        </div>
        {rows.length > 0 ? (
          <table className="qa-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Answer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`${stageBlock.stage}-${idx}`}>
                  <td>{row.question}</td>
                  <td>
                    {row.answer && String(row.answer).trim() !== "" ? (
                      row.answer
                    ) : (
                      <span className="muted">No response</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="qa-empty muted">No responses recorded.</div>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {loading ? (
        <p style={{ margin: 0, color: "#666" }}>Loading shift questions...</p>
      ) : error ? (
        <p style={{ margin: 0, color: "#b91c1c" }}>{error}</p>
      ) : session ? (
        <div>
          <div
            className="mobile-session-header"
            style={{ marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}
          >
            <div className="mobile-session-provider">
              <div className="provider-name">{session.caregiverName}</div>
              <div className="provider-role">{session.providerRole}</div>
            </div>
            <div className="mobile-session-status">
              <span className="status-pill">{session.status}</span>
              <div className="mobile-visit-datetime">{session.timeRangeLabel}</div>
            </div>
          </div>

          <div style={{ marginBottom: "12px", color: "#444", fontSize: 14 }}>
            <div style={{ marginBottom: "6px" }}>
              <strong>Date:</strong> {session.dateLabel}
            </div>
            {session.address ? (
              <div style={{ marginBottom: "6px" }}>
                <strong>Location:</strong> {session.address}
              </div>
            ) : null}
          </div>

          <div className="qa-stage-grid">{stages.map(renderStageTable)}</div>
        </div>
      ) : (
        <p style={{ margin: 0, color: "#666" }}>No shift details available.</p>
      )}
    </Modal>
  );
};

export default ShiftCheckInOutDetailModal;
